import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EnquiryModal from "@/components/EnquiryModal";
import { PageSEO } from "@/components/PageSEO";
import { PageHero } from "@/components/PageHero";
import { useLang } from "@/context/LanguageContext";
import { ExternalLink, CheckCircle, Phone, ChevronDown, ChevronUp } from "lucide-react";

const FINANCE_HERO = "https://static.prod-images.emergentagent.com/jobs/236e7b77-d75d-43f0-8b66-78eefb5d55fb/images/b74c7d837439a056e9a5e099b886a3f629d8b7405cb1dba63dcf4b9cf95e5440.jpeg";

const banks = [
  {
    name: "State Bank of India (SBI)",
    shortName: "SBI",
    type: "Government Bank",
    product: "SBI SME Term Loan / SBI Real Estate Loan",
    features: [
      "Term loans for industrial plot purchase",
      "Loan up to 75% of plot value",
      "Repayment up to 15 years",
      "Interest rate from 8.5% p.a.",
      "SIIDCUL allotted plots accepted as collateral",
    ],
    contact: "1800-11-2211",
    website: "https://www.sbi.co.in",
    color: "bg-blue-600",
  },
  {
    name: "Punjab National Bank (PNB)",
    shortName: "PNB",
    type: "Government Bank",
    product: "PNB Industrial Plot Finance",
    features: [
      "Loans for purchase of industrial plots",
      "Competitive interest rates from 8.75% p.a.",
      "Loan up to 70% of plot cost",
      "Flexible EMI options",
      "Quick processing for SIIDCUL plots",
    ],
    contact: "1800-180-2222",
    website: "https://www.pnbindia.in",
    color: "bg-red-600",
  },
  {
    name: "Union Bank of India",
    shortName: "UBI",
    type: "Government Bank",
    product: "Union MSME Industrial Loan",
    features: [
      "MSME loans including industrial land purchase",
      "Collateral-free loans up to Rs. 10 lakh (CGTMSE)",
      "Priority sector lending benefits",
      "Interest subsidy under government schemes",
      "Dedicated MSME branches at Kotdwar",
    ],
    contact: "1800-222-244",
    website: "https://www.unionbankofindia.co.in",
    color: "bg-purple-600",
  },
  {
    name: "Bank of Baroda",
    shortName: "BOB",
    type: "Government Bank",
    product: "Baroda MSME Loan / Commercial Plot Finance",
    features: [
      "Commercial & industrial plot finance",
      "Loan up to Rs. 10 Cr for large industrial plots",
      "Attractive floating rate options",
      "Simplified documentation process",
      "Support for Uttarakhand government subsidy schemes",
    ],
    contact: "1800-258-4455",
    website: "https://www.bankofbaroda.in",
    color: "bg-orange-600",
  },
  {
    name: "HDFC Bank",
    shortName: "HDFC",
    type: "Private Bank",
    product: "Commercial Property Loan",
    features: [
      "Commercial & industrial land purchase loans",
      "Fast disbursal — within 7 working days",
      "Loan up to 80% of property value",
      "Flexible tenure: 3–15 years",
      "Dedicated relationship manager",
    ],
    contact: "1800-202-6161",
    website: "https://www.hdfcbank.com",
    color: "bg-teal-600",
  },
];

const privateFinance = [
  {
    name: "HDFC Ltd.",
    type: "NBFC",
    product: "Industrial Property Finance",
    features: ["Quick loan against industrial plots", "Loan against leasehold plots", "Flexible EMI", "Up to 70% LTV"],
    website: "https://www.hdfc.com",
  },
  {
    name: "L&T Finance",
    type: "NBFC",
    product: "SME & Business Loan",
    features: ["Unsecured business loans", "Loans against industrial property", "Fast approval", "Rs. 10 Lakh to Rs. 5 Cr"],
    website: "https://www.ltfs.com",
  },
  {
    name: "Bajaj Finance",
    type: "NBFC",
    product: "Loan Against Property",
    features: ["Loan against industrial plots", "Online application", "Rs. 25 Lakh to Rs. 5 Cr", "Tenure: 1–15 years"],
    website: "https://www.bajajfinserv.in",
  },
  {
    name: "Tata Capital",
    type: "NBFC",
    product: "Commercial Property Loan",
    features: ["Industrial & commercial property loans", "Competitive rates", "Doorstep service", "Digital processing"],
    website: "https://www.tatacapital.com",
  },
];

const processSteps = [
  { step: "01", title: "Select Your Plot", desc: "Choose a plot size that fits your investment plan and business needs." },
  { step: "02", title: "Get Plot Allotment Letter", desc: "Obtain allotment letter from VS Agro Infra Enterprises after paying EMD and processing fee." },
  { step: "03", title: "Approach Bank / NBFC", desc: "Submit the allotment letter, plot documents, and business plan to lender." },
  { step: "04", title: "Pay 25% to VS Agro Infra Enterprises", desc: "Make 25% payment to VS Agro Infra Enterprises so you're eligible for mortgage." },
  { step: "05", title: "Bank Disburses Loan", desc: "Bank disburses loan after SIIDCUL NOC, paying balance land cost." },
  { step: "06", title: "Start Construction", desc: "Begin building your industrial unit with remaining loan amount." },
];

export default function Finance() {
  const [openBank, setOpenBank] = useState(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const { t } = useLang();

  return (
    <div className="bg-[#0F172A]">
      <PageSEO page="finance" />
      <Navbar />

      {/* Header */}
      <PageHero
        image={FINANCE_HERO}
        tag="Bank & NBFC Finance"
        title={t("finance.title")}
        subtitle={t("finance.subtitle")}
        breadcrumb={{ home: t("nav.home"), current: t("finance.breadcrumb") }}
      />

      {/* Mortgage Info */}
      <div className="bg-orange-500 py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-white text-sm text-center font-medium">
            {t("finance.mortgageInfo")}
          </p>
        </div>
      </div>

      {/* Bank Finance */}
      <div className="bg-[#0F172A] py-12" data-testid="banks-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-orange-500 font-semibold text-sm uppercase tracking-widest">{t("finance.financingPartners")}</span>
            <div className="section-divider mt-2"></div>
            <h2 className="text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: "2rem", textTransform: "uppercase" }}>
              {t("finance.bankFinance")}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {banks.map((bank) => (
              <div
                key={bank.name}
                className="finance-card bg-[#1E293B] overflow-hidden flex flex-col"
                data-testid={`bank-card-${bank.shortName}`}
              >
                <div className={`${bank.color} px-5 py-3 flex items-center gap-2`}>
                  <div className="w-8 h-8 bg-white/20 flex items-center justify-center text-white font-black text-xs">
                    {bank.shortName}
                  </div>
                  <div>
                    <div className="text-white font-bold text-sm">{bank.name}</div>
                    <div className="text-white/70 text-xs">{bank.type}</div>
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="text-xs font-semibold text-orange-400 uppercase tracking-wide mb-3">
                    {bank.product}
                  </div>
                  <ul className="space-y-2 mb-4 flex-1">
                    {bank.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-2 mt-auto">
                    <a
                      href={`tel:${bank.contact}`}
                      className="flex-1 flex items-center justify-center gap-1.5 border border-white/10 text-slate-300 py-2 text-xs font-semibold hover:border-orange-400 hover:text-orange-400 transition-colors"
                    >
                      <Phone className="w-3.5 h-3.5" /> {bank.contact}
                    </a>
                    <a
                      href={bank.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-1 border border-white/10 text-slate-300 px-3 py-2 text-xs font-semibold hover:border-orange-400 hover:text-orange-400 transition-colors"
                      data-testid={`bank-link-${bank.shortName}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Private Finance */}
      <div className="bg-[#1E293B] py-12 border-t border-white/[0.06]" data-testid="private-finance-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: "2rem", textTransform: "uppercase" }}>
              {t("finance.privateFinance")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {privateFinance.map((pf) => (
              <div
                key={pf.name}
                className="finance-card bg-[#0F172A] p-5"
                data-testid={`private-finance-${pf.name.replace(/\s/g, "-")}`}
              >
                <div className="font-bold text-white text-sm mb-0.5" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.1rem", textTransform: "uppercase" }}>
                  {pf.name}
                </div>
                <div className="text-xs text-orange-400 font-semibold mb-3">{pf.type} &nbsp;·&nbsp; {pf.product}</div>
                <ul className="space-y-1.5 mb-4">
                  {pf.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-400">
                      <span className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-1.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href={pf.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-orange-500 font-semibold hover:text-orange-400 transition-colors"
                >
                  Visit Website <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Process */}
      <div className="bg-slate-900 py-12" data-testid="finance-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2
              className="text-white"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: "2rem", textTransform: "uppercase" }}
            >
              {t("finance.howToFinance")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {processSteps.map((s) => (
              <div key={s.step} className="flex gap-4">
                <div
                  className="text-orange-500 font-black text-2xl flex-shrink-0"
                  style={{ fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}
                >
                  {s.step}
                </div>
                <div>
                  <div className="text-white font-semibold text-sm mb-1">{s.title}</div>
                  <div className="text-slate-400 text-xs leading-relaxed">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <button
              onClick={() => setEnquiryOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 font-bold uppercase tracking-wider text-sm transition-colors"
              data-testid="finance-enquire-btn"
            >
              {t("finance.getAssistance")}
            </button>
          </div>
        </div>
      </div>

      <Footer />
      <EnquiryModal isOpen={enquiryOpen} onClose={() => setEnquiryOpen(false)} selectedPlot={null} />
    </div>
  );
}
